import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Header as HeaderSuite, Dropdown, Avatar } from 'rsuite'
import MenuIcon from '@rsuite/icons/Menu'
import ArrowDownIcon from '@rsuite/icons/ArrowDown'
import HomeIcon from '@rsuite/icons/legacy/Home'
import SignOutIcon from '@rsuite/icons/legacy/SignOut'

import { useAuth } from 'contexts/AuthContext'
import { getMenuItemByPath } from 'config/MenuItems'
import { getDashboardPath } from 'utils'
import tenantConfig from 'config/tenant'
import ConfirmModal from '../modal/confirmModal/ConfirmModal'
import './header.scss'

interface HeaderProps {
  onMenuToggle?: () => void
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { role, user, logout } = useAuth()
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const pageTitle =
    getMenuItemByPath(location.pathname, role)?.name ?? 'Dashboard'

  const handleConfirmLogout = () => {
    setLogoutConfirmOpen(false)
    logout(true)
    navigate('/login', { replace: true })
  }

  const displayName = user?.fullName || user?.email || 'User'
  const roleLabel = role
    ? `${role.replace('ROLE_', '').slice(0, 1)}${role.replace('ROLE_', '').slice(1).toLowerCase()}`
    : ''
  const initials = displayName.charAt(0).toUpperCase()

  return (
    <HeaderSuite className="header">
      <div className="header__content">
        {onMenuToggle && (
          <button
            type="button"
            className="header__menu-toggle"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <MenuIcon />
          </button>
        )}
        <Link to={getDashboardPath()} className="header__brand">
          <img
            src={tenantConfig.logoTransparent}
            className="header__brand-logo"
            alt={tenantConfig.name}
          />
        </Link>
        <div className="header__main">
          <h1 className="header__title">{pageTitle}</h1>
          <Dropdown
            className="header__user-menu"
            placement="bottomEnd"
            trigger="click"
            noCaret
            renderToggle={(props, ref) => (
              <button
                {...props}
                ref={ref as React.Ref<HTMLButtonElement>}
                type="button"
                className="header__user-toggle"
              >
                <Avatar circle size="sm" className="header__avatar">
                  {initials}
                </Avatar>
                <div className="header__user-info">
                  <span className="header__user-name">{displayName}</span>
                  <span className="header__user-role">{roleLabel}</span>
                </div>
                <ArrowDownIcon className="header__user-chevron" />
              </button>
            )}
          >
            <Dropdown.Item icon={<HomeIcon />} onClick={() => navigate('/')}>
              Home
            </Dropdown.Item>
            <Dropdown.Separator />
            <Dropdown.Item
              icon={<SignOutIcon />}
              onClick={() => setLogoutConfirmOpen(true)}
            >
              Sign Out
            </Dropdown.Item>
          </Dropdown>
        </div>
      </div>

      <ConfirmModal
        open={logoutConfirmOpen}
        title="Sign Out"
        message="Are you sure you want to log out?"
        confirmLabel="Yes, Log Out"
        cancelLabel="Cancel"
        onConfirm={handleConfirmLogout}
        onCancel={() => setLogoutConfirmOpen(false)}
      />
    </HeaderSuite>
  )
}

export default Header
